// schema
import * as ISchema from "@schema";

// libraries
import * as React from "react";
import { connect } from "react-redux";
import { AssistantTabCard } from "@clockwise/web-commons/src/ui/assistant-tab-card";
import { CheckCircle, RadioButtonUnchecked } from "@material-ui/icons";
import {
  createChecklistFromOrg,
  OnboardingChecklistTypes,
  OnboardingChecklistLabels,
  sortedChecklistItems,
  numberOfOnboardingChecklistItemsComplete,
  OnboardingChecklistSimplified,
} from "./OnboardingChecklistCard.util";

import { SegmentedBar } from "@components/segmented-bar";
import { IReduxState } from "@reducers/root.reducer";
import { setActiveView } from "@actions/chrome-extension.actions";
import { ChromeAppWrapperView } from "@util/chrome-extension.util";
import { setSettingsDialogOpen } from "@actions/chrome-extension.actions";
import {
  openIntegrations,
  openSmartHolds,
  openInvites,
  toggleExpanded,
  IOnboardingChecklistSyncType,
  setSavedOnboardingChecklistSyncType,
} from "@actions/settings.actions";
import { isEmpty } from "lodash";
import { ChecklistConfetti } from "./ChecklistConfetti";

interface IProps extends IContainer {
  setActiveView: typeof setActiveView;
  setSettingsDialogOpen: typeof setSettingsDialogOpen;
  openIntegrations: typeof openIntegrations;
  openSmartHolds: typeof openSmartHolds;
  openInvites: typeof openInvites;
  toggleExpanded: typeof toggleExpanded;
  requiresSaveLunchSync: boolean;
  requiresSaveFocusTimeSync: boolean;
  savedOnboardingChecklistSyncType: string;
  setSavedOnboardingChecklistSyncType: typeof setSavedOnboardingChecklistSyncType;
}

interface IContainer {
  org: ISchema.IOrg;
  onClose: () => void;
}

const TOTAL_ITEMS = 5;

export const OnboardingChecklistCardCmpt = (props: IProps) => {
  const {
    org,
    requiresSaveLunchSync,
    requiresSaveFocusTimeSync,
    savedOnboardingChecklistSyncType,
    setSavedOnboardingChecklistSyncType,
  } = props;

  const [checklist, setChecklist] = React.useState<OnboardingChecklistSimplified>(
    createChecklistFromOrg(org),
  );

  const isChecklistComplete = numberOfOnboardingChecklistItemsComplete(checklist) === TOTAL_ITEMS;

  // Lifecycle

  React.useEffect(() => {
    let newChecklist: OnboardingChecklistSimplified = { ...checklist };

    if (!isEmpty(savedOnboardingChecklistSyncType)) {
      if (savedOnboardingChecklistSyncType === IOnboardingChecklistSyncType.slackEnabled) {
        newChecklist["slackEnabled"] = true;
      } else if (savedOnboardingChecklistSyncType === IOnboardingChecklistSyncType.inviteSent) {
        newChecklist["inviteSent"] = true;
      }
      setChecklist(newChecklist);
      setSavedOnboardingChecklistSyncType(""); //reset redux listener
    }
  }, [savedOnboardingChecklistSyncType]);

  React.useEffect(() => {
    if (requiresSaveLunchSync) {
      let newChecklist: OnboardingChecklistSimplified = { ...checklist };
      newChecklist["lunchEnabled"] = !checklist["lunchEnabled"]; //is a toggle in settings
      setChecklist(newChecklist);
    }
  }, [requiresSaveLunchSync]);

  React.useEffect(() => {
    if (requiresSaveFocusTimeSync) {
      let newChecklist: OnboardingChecklistSimplified = { ...checklist };
      newChecklist["focusTimeEnabled"] = !checklist["focusTimeEnabled"]; //is a toggle in settings
      setChecklist(newChecklist);
    }
  }, [requiresSaveFocusTimeSync]);

  // Helpers
  const calculatePercentageString = (numerator: number, denomenator: number) => {
    const num = (numerator / denomenator) * 100;
    return `${num}%`;
  };

  const getChecklistItemOnClick = (itemType: OnboardingChecklistTypes) => {
    switch (itemType) {
      case OnboardingChecklistTypes.lunchEnabled:
        props.setSettingsDialogOpen(true);
        props.toggleExpanded("expandLunch");
        break;
      case OnboardingChecklistTypes.flexMeetingEnabled:
        props.setActiveView(ChromeAppWrapperView.AutopilotList);
        break;
      case OnboardingChecklistTypes.focusTimeEnabled:
        props.setSettingsDialogOpen(true);
        props.openSmartHolds();
        break;
      case OnboardingChecklistTypes.slackEnabled:
        props.setSettingsDialogOpen(true);
        props.openIntegrations();
        break;
      case OnboardingChecklistTypes.inviteSent:
        props.setSettingsDialogOpen(true);
        // TODO: Change to open new invite modal when rishmas PR is merged
        props.openInvites();
        break;
      default:
        break;
    }
  };
  const getSegments = (complete: number, total: number) => {
    return [
      { color: "#23A7BC", value: complete },
      { color: "#EEF0F1", value: total - complete },
    ];
  };
  // Renderers

  const CardBody = () => {
    const numItemsComplete = numberOfOnboardingChecklistItemsComplete(checklist);
    return (
      <div className="cw-flex cw-flex-col">
        <div className="cw-font-brand cw-text-dusk-darker cw-font-semibold cw-leading-6">
          Make the most out of your trial
        </div>
        <div className="cw-py-2 cw-flex cw-font-body cw-text-sky-standard cw-font-semibold cw-items-center">
          <span className="cw-pr-2">
            {calculatePercentageString(numItemsComplete, TOTAL_ITEMS)}{" "}
          </span>
          {/* Using tailwind for the wrapper div for <SegmentedBar/> wasn't working - SegmentedBar only picked up the height if I set it via style */}
          <div style={{ position: "relative", width: "100%", height: "10px" }}>
            <SegmentedBar segments={getSegments(numItemsComplete, TOTAL_ITEMS)} animateIn />
          </div>
        </div>
        <div className={isChecklistComplete ? "" : "cw-py-2"}>
          {" "}
          {isChecklistComplete ? <ChecklistConfetti /> : renderChecklist()}{" "}
        </div>
      </div>
    );
  };

  const renderChecklistItem = (checklistItem: OnboardingChecklistTypes) => {
    const isComplete = checklist[checklistItem];
    const fontColor = isComplete ? "cw-text-slate-lighter" : "cw-text-dusk-darker";
    return (
      <div
        className={`cw-flex cw-font-body cw-width-full cw-items-center cw-py-1 ${fontColor}`}
        key={checklistItem}
      >
        <div className={"cw-pr-2"}>
          {isComplete ? (
            <CheckCircle className="cw-fill-sky-darker" />
          ) : (
            <RadioButtonUnchecked className="cw-fill-slate-lightest" />
          )}
        </div>
        <div
          className={
            isComplete
              ? ""
              : "cw-border-dashed cw-border-b-2 cw-border-slate-lighter cw-cursor-pointer"
          }
          onClick={isComplete ? undefined : () => getChecklistItemOnClick(checklistItem)}
        >
          {OnboardingChecklistLabels[checklistItem]}
        </div>
      </div>
    );
  };

  const renderChecklist = () => {
    const sortedChecklistKeys = sortedChecklistItems(checklist);
    return sortedChecklistKeys.map((key: OnboardingChecklistTypes) => renderChecklistItem(key));
  };

  return (
    <AssistantTabCard
      bodyComponent={CardBody()}
      tooltipText={"Tip · Now"}
      onClear={isChecklistComplete ? () => props.onClose() : undefined} // undefined will hide the X icongit
    />
  );
};

function mapStateToProps(state: IReduxState, _ownProps: IContainer) {
  return {
    requiresSaveLunchSync: state.settings.requiresSaveLunchSync,
    requiresSaveFocusTimeSync: state.settings.requiresSaveFocusTimeSync,
    savedOnboardingChecklistSyncType: state.settings.savedOnboardingChecklistSyncType,
  };
}

export const OnboardingChecklistCard = connect(mapStateToProps, {
  setActiveView,
  setSettingsDialogOpen,
  openIntegrations,
  openSmartHolds,
  openInvites,
  toggleExpanded,
  setSavedOnboardingChecklistSyncType,
})(OnboardingChecklistCardCmpt);
